import { z } from 'zod';

/**
 * One-shot router state carried when a surface sends the viewer to the agents
 * index on purpose.
 *
 * The index normally opens the most recent run when it loads without an agent
 * id in the URL, which is what makes a fresh visit useful. That default is
 * wrong after a delete: the run you were looking at is gone, and yanking the
 * next one into view reads as "the wrong agent opened itself". `skipAutoSelect`
 * turns the auto-open off for that single navigation so the index settles on
 * its no-agent-selected screen instead.
 */
export const AgentsIndexRouteStateSchema = z.object({
  skipAutoSelect: z.boolean().optional(),
});
export type AgentsIndexRouteState = z.infer<typeof AgentsIndexRouteStateSchema>;

/** The state to navigate with when the index must not auto-open a run. */
export const SKIP_AUTO_SELECT_STATE: AgentsIndexRouteState = { skipAutoSelect: true };

/**
 * Parse the router's untyped `location.state`. Anything that is not the flag
 * (null, a legacy state shape, another page's state) means "auto-select is
 * fine", so the normal landing behavior survives.
 */
export function shouldSkipAutoSelect(state: unknown): boolean {
  const parsed = AgentsIndexRouteStateSchema.safeParse(state);
  return parsed.success && parsed.data.skipAutoSelect === true;
}
